import { NavLink } from 'react-router';
import { CountBadge, Icon, cx, type NavItem } from '@saas/ui';
import type { NavModel } from './nav.js';

/**
 * デスクトップの左サイドバー（240px）。モバイルでは Header のメニューシートの中身として再利用する。
 * 選択中はアイコンとラベルを text/primary + bg/sunken。下線・色は使わない（05 §4.6）。
 */
function Item({ item, count, onNavigate }: { item: NavItem; count?: number; onNavigate?: () => void }) {
  return (
    <li>
      <NavLink
        to={item.to}
        end={item.end}
        target={item.newWindow ? '_blank' : undefined}
        rel={item.newWindow ? 'noopener noreferrer' : undefined}
        onClick={onNavigate}
        className={({ isActive }) =>
          cx('flex min-tap items-center gap-3 rounded-input px-3 text-body transition-colors', isActive ? 'bg-brand-subtle font-semibold text-brand' : 'text-fg-secondary hover:bg-sunken hover:text-fg')
        }
      >
        <Icon name={item.icon} size={24} className="shrink-0" />
        <span className="min-w-0 flex-1 wrap-name">{item.label}</span>
        {count ? <CountBadge count={count} label={item.label} /> : null}
        {item.newWindow ? <Icon name="open_in_new" size={20} className="shrink-0" /> : null}
      </NavLink>
    </li>
  );
}

function Section({ title, items, counts, onNavigate }: { title?: string; items: NavItem[]; counts: Record<string, number>; onNavigate?: () => void }) {
  if (items.length === 0) return null;
  return (
    <section className="flex flex-col gap-1">
      {title ? <h2 className="px-3 pb-1 pt-4 text-caption font-medium text-fg-tertiary">{title}</h2> : null}
      <ul className="flex flex-col gap-0.5">
        {items.map((it) => (
          <Item key={it.to} item={it} count={it.badge ? counts[it.badge] : undefined} onNavigate={onNavigate} />
        ))}
      </ul>
    </section>
  );
}

export function SidebarNav({ model, onNavigate }: { model: NavModel; onNavigate?: () => void }) {
  return (
    <nav aria-label="メインメニュー" className="flex flex-col gap-1 pb-6">
      <Section items={model.primary} counts={model.counts} onNavigate={onNavigate} />
      {model.work.map((g) => (
        <Section key={g.module} title={g.label} items={g.items} counts={model.counts} onNavigate={onNavigate} />
      ))}
      <Section title="設定" items={[{ label: '設定', to: '/settings', icon: 'settings' }]} counts={model.counts} onNavigate={onNavigate} />
    </nav>
  );
}

export function Sidebar({ model }: { model: NavModel }) {
  return (
    <aside className="sticky top-[var(--header-height)] hidden h-[calc(100dvh-var(--header-height))] w-[var(--sidebar-width)] shrink-0 overflow-y-auto border-r border-line bg-card px-3 py-4 lg:block">
      <SidebarNav model={model} />
    </aside>
  );
}
